import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import ResourceList from "../../../components/resourceList/ResourceList";
import Input from "../../../components/input/Input";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import styles from "./InvoicePrefixesScreen.module.scss";
import {
  fetchInvoicePrefixes,
  createInvoicePrefix,
  updateInvoicePrefix,
  deleteInvoicePrefix,
  createInvoicePrefixResetState,
  updateInvoicePrefixResetState,
  deleteInvoicePrefixResetState,
} from "../../../store/actions/admin/invoicePrefixes";
import _ from "lodash";

const HEADER_VALUES = [
  { label: "PREFIKS", key: "prefix", sortable: true },
  { label: "OPIS", key: "description", sortable: false },
  { label: "PODRAZUMEVANI", key: "isDefault", sortable: true },
  { label: "AKTIVAN", key: "isActive", sortable: true },
  { label: "KREIRANO", key: "createdAt", sortable: true },
  { label: "AKCIJE", sortable: false },
];

const InvoicePrefixesScreen = () => {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);
  
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState(null);
  const [sortBy, setSortBy] = useState("id");
  const [order, setOrder] = useState("DESC");
  
  const prefixes = useSelector((state) => state.invoicePrefixes.prefixes);
  const createPrefix = useSelector((state) => state.invoicePrefixes.createPrefix);
  const updatePrefix = useSelector((state) => state.invoicePrefixes.updatePrefix);
  const deletePrefix = useSelector((state) => state.invoicePrefixes.deletePrefix);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState(null);
  
  // Form states
  const [prefix, setPrefix] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    isMountedRef.current = true;
    loadData();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, isActiveFilter, sortBy, order]);

  useEffect(() => {
    if (createPrefix.success) {
      toast.success("Prefiks uspešno kreiran");
      dispatch(createInvoicePrefixResetState());
      setCreateModalOpen(false);
      resetForm();
      loadData();
    }
    if (createPrefix.error) {
      toast.error(createPrefix.error);
      dispatch(createInvoicePrefixResetState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createPrefix.success, createPrefix.error]);

  useEffect(() => {
    if (updatePrefix.success) {
      toast.success("Prefiks uspešno ažuriran");
      dispatch(updateInvoicePrefixResetState());
      setEditModalOpen(false);
      resetForm();
      loadData();
    }
    if (updatePrefix.error) {
      toast.error(updatePrefix.error);
      dispatch(updateInvoicePrefixResetState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePrefix.success, updatePrefix.error]);

  useEffect(() => {
    if (deletePrefix.success) {
      toast.success("Prefiks uspešno obrisan");
      dispatch(deleteInvoicePrefixResetState());
      setDeleteModalOpen(false);
      setSelectedPrefix(null);
      loadData();
    }
    if (deletePrefix.error) {
      toast.error(deletePrefix.error);
      dispatch(deleteInvoicePrefixResetState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletePrefix.success, deletePrefix.error]);

  const loadData = () => {
    if (isMountedRef.current) {
      dispatch(fetchInvoicePrefixes(page, perPage, isActiveFilter, searchTerm, sortBy, order));
    }
  };

  const handleSearch = _.debounce((value) => {
    setSearchTerm(value);
    setPage(1);
  }, 600);

  const handleSearchChange = (value) => {
    handleSearch(value);
  };

  const handleIsActiveFilterToggle = () => {
    if (isActiveFilter === null) {
      setIsActiveFilter(true);
    } else if (isActiveFilter === true) {
      setIsActiveFilter(false);
    } else {
      setIsActiveFilter(null);
    }
    setPage(1);
  };

  const onLoadPage = (newPage) => {
    setPage(newPage);
  };

  const onSort = (newSortBy, newOrder) => {
    setSortBy(newSortBy);
    setOrder(newOrder);
    setPage(1);
  };

  const resetForm = () => {
    setPrefix("");
    setDescription("");
    setIsDefault(false);
    setIsActive(true);
    setSelectedPrefix(null);
  };

  const handleCreateClick = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const handleEditClick = (prefixItem) => {
    setSelectedPrefix(prefixItem);
    setPrefix(prefixItem.prefix || "");
    setDescription(prefixItem.description || "");
    setIsDefault(prefixItem.isDefault || false);
    setIsActive(prefixItem.isActive !== undefined ? prefixItem.isActive : true);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (prefixItem) => {
    setSelectedPrefix(prefixItem);
    setDeleteModalOpen(true);
  };

  const handleCreate = async () => {
    if (!prefix.trim()) {
      toast.error("Molimo unesite prefiks");
      return;
    }

    const result = await dispatch(
      createInvoicePrefix({
        prefix: prefix.trim(),
        description: description.trim() || null,
        isDefault,
        isActive,
      })
    );

    if (!result.success) {
      // Error toast is handled in useEffect
      return;
    }
  };

  const handleUpdate = async () => {
    if (!prefix.trim()) {
      toast.error("Molimo unesite prefiks");
      return;
    }

    const result = await dispatch(
      updateInvoicePrefix(selectedPrefix.id, {
        prefix: prefix.trim(),
        description: description.trim() || null,
        isDefault,
        isActive,
      })
    );

    if (!result.success) {
      // Error toast is handled in useEffect
      return;
    }
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteInvoicePrefix(selectedPrefix.id));
    if (!result.success) {
      // Error toast is handled in useEffect
      return;
    }
  };

  const renderPrefixRow = (prefixItem, index) => {
    return (
      <div key={prefixItem.id} className={styles.row}>
        <div className={styles.cell}>{prefixItem.prefix}</div>
        <div className={styles.cell}>{prefixItem.description || "-"}</div>
        <div className={styles.cell}>
          <span className={prefixItem.isDefault ? styles.defaultBadge : styles.notDefaultBadge}>
            {prefixItem.isDefault ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          <span className={prefixItem.isActive ? styles.activeBadge : styles.inactiveBadge}>
            {prefixItem.isActive ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          {prefixItem.createdAt
            ? new Date(prefixItem.createdAt).toLocaleDateString("sr-RS")
            : "-"}
        </div>
        <div className={styles.cell}>
          <div className={styles.actions}>
            <button
              className={styles.editButton}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(prefixItem);
              }}
            >
              Izmeni
            </button>
            <button
              className={styles.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(prefixItem);
              }}
              disabled={prefixItem.isDefault}
              title={prefixItem.isDefault ? "Ne možete obrisati podrazumevani prefiks" : ""}
            >
              Obriši
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Prefiksi faktura</h1>
          <div className={styles.headerActions}>
            <Button name="+ Novi prefiks" onClick={handleCreateClick} />
          </div>
        </div>

        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Aktivan:</label>
            <button
              className={`${styles.toggleButton} ${
                isActiveFilter === true
                  ? styles.toggleActive
                  : isActiveFilter === false
                  ? styles.toggleInactive
                  : ""
              }`}
              onClick={handleIsActiveFilterToggle}
            >
              {isActiveFilter === null && "Sve"}
              {isActiveFilter === true && "Aktivni"}
              {isActiveFilter === false && "Neaktivni"}
            </button>
          </div>
          <div className={styles.searchGroup}>
            <Input
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Pretraži prefikse..."
            />
          </div>
        </div>

        <div className={styles.tableSection}>
          <ResourceList
            resource={{
              ...prefixes.data,
              attempt: prefixes.attempt,
            }}
            onSort={onSort}
            onLoadPage={onLoadPage}
            headerValues={HEADER_VALUES}
            rowRender={renderPrefixRow}
            showLoader={true}
          />
        </div>
      </div>

      {/* Create Modal */}
      <BaseModal
        modalOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          resetForm();
        }}
      >
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>Novi prefiks</h2>
          <div className={styles.modalForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Prefiks *</label>
              <Input
                value={prefix}
                onChange={setPrefix}
                placeholder="Npr. INV-, BILL-, CUST-"
              />
              <p className={styles.helperText}>
                Preporučuje se završiti prefiks sa crticom (-)
              </p>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Opis</label>
              <textarea
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Opis prefiksa (opciono)"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className={styles.checkbox}
                />
                Podrazumevani prefiks
              </label>
              <p className={styles.helperText}>
                Podrazumevani prefiks se koristi kada šema fakture nema dodeljen prefiks
              </p>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className={styles.checkbox}
                />
                Aktivan
              </label>
            </div>
            <div className={styles.modalActions}>
              <Button
                name={createPrefix.attempt ? "Kreiranje..." : "Kreiraj"}
                onClick={handleCreate}
                disabled={createPrefix.attempt}
                showLoader={createPrefix.attempt}
              />
              <Button
                name="Otkaži"
                onClick={() => {
                  setCreateModalOpen(false);
                  resetForm();
                }}
              />
            </div>
          </div>
        </div>
      </BaseModal>

      {/* Edit Modal */}
      <BaseModal
        modalOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          resetForm();
        }}
      >
        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>Izmeni prefiks</h2>
          <div className={styles.modalForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Prefiks *</label>
              <Input
                value={prefix}
                onChange={setPrefix}
                placeholder="Npr. INV-, BILL-, CUST-"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Opis</label>
              <textarea
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Opis prefiksa (opciono)"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className={styles.checkbox}
                />
                Podrazumevani prefiks
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className={styles.checkbox}
                />
                Aktivan
              </label>
            </div>
            <div className={styles.modalActions}>
              <Button
                name={updatePrefix.attempt ? "Čuvanje..." : "Sačuvaj"}
                onClick={handleUpdate}
                disabled={updatePrefix.attempt}
                showLoader={updatePrefix.attempt}
              />
              <Button
                name="Otkaži"
                onClick={() => {
                  setEditModalOpen(false);
                  resetForm();
                }}
              />
            </div>
          </div>
        </div>
      </BaseModal>

      {/* Delete Modal */}
      <AreYouSureModal
        modalOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedPrefix(null);
        }}
        onConfirm={handleDelete}
        message={`Da li ste sigurni da želite da obrišete prefiks "${selectedPrefix?.prefix}"?`}
        attempt={deletePrefix.attempt}
      />
    </DashboardLayout>
  );
};

export default InvoicePrefixesScreen;

